import b from 'b_';
import cx from 'classnames';
import elementResizeDetectorMaker from 'element-resize-detector';
import React from 'react';

import './scale.scss';

export interface ScaleProps {
    //@ts-ignore - EDUCATION-37077: [react-update]: fixme typings: ???
    as: React.ReactType;
    baseWidth?: number;
    width?: number;
    maxWidth?: number;
    minWidth?: number;
    mix?: string;
    fitMode?: boolean;
    onScale?: (scale: number) => void;
}

interface State {
    scale: number;
    height: string;
    width: string;
    initialized: boolean;
}

export class Scale extends React.Component<React.PropsWithChildren<ScaleProps>, State> {
    static defaultProps = {
        as: 'div',
        fitMode: false,
    };

    public state = {
        scale: 1,
        height: 'auto',
        width: 'auto',
        initialized: false,
    };

    componentDidMount() {
        this.erd = elementResizeDetectorMaker({ strategy: 'scroll' });
        this.erd.listenTo(this.containerRef.current!, this.handleResize);
        this.erd.listenTo(this.contentRef.current!, this.handleResize);

        this.handleResize();

        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({ initialized: true });
    }

    componentDidUpdate(prevProps: ScaleProps, prevState: State) {
        if (this.state.width !== prevState.width) {
            this.handleResize();
        }
    }

    componentWillUnmount() {
        this.erd.uninstall(this.containerRef.current!);
        this.erd.uninstall(this.contentRef.current!);
    }

    private erd: elementResizeDetectorMaker.Erd;

    private containerRef = React.createRef<HTMLDivElement>();
    private contentRef = React.createRef<HTMLDivElement>();

    setInitialScale = () => {
        this.setState({
            scale: 1,
            height: 'auto',
            width: 'auto',
        });

        if (this.props.onScale) {
            this.props.onScale(1);
        }
    };

    setScale = (scaleWidth: number, containerWidth: number) => {
        const scale = containerWidth / scaleWidth;
        const contentHeight = this.contentRef.current!.offsetHeight;
        this.setState({
            scale,
            height: `${contentHeight * scale}px`,
            width: `${scaleWidth}px`,
        });

        if (this.props.onScale) {
            this.props.onScale(scale);
        }
    };

    getCompensatedWidths = () => {
        const { width, minWidth, maxWidth, baseWidth } = this.props;

        const containerWidth = this.containerRef.current!.offsetWidth;
        const compensationScale = baseWidth ? baseWidth / containerWidth : 1;

        const [compensatedWidth, compensatedMinWidth, compensatedMaxWidth] = [width, minWidth, maxWidth].map((item) => {
            return item ? item * compensationScale : item;
        });

        return { compensatedWidth, compensatedMinWidth, compensatedMaxWidth };
    };

    handleResize = () => {
        const { fitMode, baseWidth, width } = this.props;
        const containerWidth = this.containerRef.current!.offsetWidth;

        if (fitMode && baseWidth && width) {
            const fitWidth = Math.min(width, containerWidth);
            const fitScale = containerWidth / fitWidth;
            this.setScale(baseWidth * fitScale, containerWidth);
            return;
        }

        const { compensatedWidth, compensatedMinWidth, compensatedMaxWidth } = this.getCompensatedWidths();

        if (compensatedWidth) {
            this.setScale(compensatedWidth, containerWidth);
        } else if (compensatedMinWidth && containerWidth < compensatedMinWidth) {
            this.setScale(compensatedMinWidth, containerWidth);
        } else if (compensatedMaxWidth && containerWidth > compensatedMaxWidth) {
            this.setScale(compensatedMaxWidth, containerWidth);
        } else {
            this.setInitialScale();
        }
    };

    render() {
        const { height, scale, width, initialized } = this.state;
        const { as: As, mix } = this.props;

        const containerStyle = { height };
        const contentStyle = {
            width,
            transform: `scale(${scale})`,
            willChange: 'transform',
        } as React.CSSProperties;

        return (
            <As
                className={cx(b('scale__container', { initialized }), mix)}
                ref={this.containerRef}
                style={containerStyle}
            >
                <div className="scale__content" ref={this.contentRef} style={contentStyle}>
                    {this.props.children}
                </div>
            </As>
        );
    }
}

export default Scale;
